import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { environment } from 'client/environments/environment';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly enabled = !!environment.googleTagManagerId; // presence of ID for reference (not used to inject now)
  private readonly debug = (environment as any).enableGtmDebug === true;
  private readonly isBrowser: boolean;

  constructor(
    private router: Router,
    private title: Title,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) return;

    // Central page_view tracking
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        this.trackPageView();
      });

    // Ensure dataLayer exists early for events before GTM fully loads.
    (window as any).dataLayer = (window as any).dataLayer || [];
  }

  // Placeholder; rely on Cookiebot/GTM consent. Adjust if you want to skip certain events prior to consent.
  private hasConsent(): boolean {
    if (!this.isBrowser) return false;
    const cb = (window as any).Cookiebot;
    if (cb && cb.consent) {
      // Example: treat statistics consent as sufficient for analytics events.
      return !!cb.consent.statistics;
    }
    // If Cookiebot not loaded yet, we can still push; GTM will block tags until consent.
    return true;
  }

  private push(data: Record<string, any>): void {
    if (!this.isBrowser) return;
    if (!this.enabled) {
      if (this.debug) console.debug('[GTM] No container ID configured');
    }
    if (!this.hasConsent()) {
      if (this.debug)
        console.debug(
          '[GTM] Consent not granted (Cookiebot), event queued but will be ignored by blocked tags',
          data
        );
    }
    try {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push(data);
      if (this.debug) console.debug('[GTM] dataLayer push', data);
    } catch (err) {
      if (this.debug) console.warn('[GTM] dataLayer push error', err);
    }
  }

  public trackPageView(): void {
    if (!this.isBrowser) return;
    this.push({
      event: 'page_view',
      page_path: window.location.pathname,
      page_title: this.title.getTitle(),
    });
  }

  public trackEvent(event: string, extra?: Record<string, any>): void {
    this.push({ event, page_title: this.title.getTitle(), ...(extra || {}) });
  }

  public trackOpenApplication(): void {
    this.trackEvent('button_open_application');
  }
  public trackApplyImmediately(): void {
    this.trackEvent('button_apply_immediately');
  }
  public trackSubmitApplication(): void {
    this.trackEvent('button_submit_application');
  }

  /** Debug helper to log current GTM / dataLayer & Cookiebot state */
  public logGtmState(): void {
    if (!this.isBrowser) return;
    const id = environment.googleTagManagerId;
    const dl = (window as any).dataLayer;
    const keys = Object.keys((window as any).google_tag_manager || {});
    const cb = (window as any).Cookiebot;
    console.info('[GTM] State', {
      id,
      containerKeys: keys,
      dataLayerLength: Array.isArray(dl) ? dl.length : 0,
      consentStats: cb?.consent?.statistics,
      consentMarketing: cb?.consent?.marketing,
      rawConsent: cb?.consent || null,
    });
    if (Array.isArray(dl)) {
      console.info('[GTM] Last events', dl.slice(-5));
    }
  }

  /** Specific consent log helper */
  public logConsent(): void {
    if (!this.isBrowser) return;
    const cb = (window as any).Cookiebot;
    if (!cb) {
      console.info('[Cookiebot] Not loaded');
      return;
    }
    console.info('[Cookiebot] Consent object', cb.consent);
  }
}
